import unfetch from 'isomorphic-unfetch';
import { jwtDecode } from 'jwt-decode';
import Cookies from 'universal-cookie';

const getJwtAuthTokenStatus = (token, refreshToken) => {
  const tokenInfo = token ? jwtAuthTokenFromString(token) : void 0;
  if (!tokenInfo) {
    return {
      status: "absent"
    };
  }
  if (tokenInfo.percentageExpired <= 0.8 && tokenInfo.ageInSeconds <= 2700) {
    return {
      status: "active"
    };
  }
  const refreshTokenInfo = refreshToken ? jwtAuthTokenFromString(refreshToken) : void 0;
  if (tokenInfo.percentageExpired >= 1 && (!refreshTokenInfo || refreshTokenInfo.percentageExpired >= 1)) {
    return {
      status: "expired"
    };
  } else {
    return {
      status: "expiring"
    };
  }
};
const jwtAuthTokenFromString = (token) => {
  let decodedToken = null;
  try {
    decodedToken = jwtDecode(token.replace("Bearer ", ""));
  } catch (_error) {
    return void 0;
  }
  return decodedToken ? {
    ...decodedToken,
    ...jwtAuthTokenExpiryInfo(decodedToken)
  } : void 0;
};
const jwtAuthRefreshTokenFromString = (token) => {
  let decodedToken = null;
  try {
    decodedToken = jwtDecode(token.replace("Bearer ", ""));
  } catch (_error) {
    return void 0;
  }
  return decodedToken ? {
    ...decodedToken,
    ...jwtAuthTokenExpiryInfo(decodedToken)
  } : void 0;
};
const jwtAuthTokenExpiryInfo = (token) => {
  const nowInSeconds = Date.now() / 1e3;
  const ttlInSeconds = token.exp - token.iat;
  const ageInSeconds = nowInSeconds - token.iat;
  const secondsUntilExpiration = nowInSeconds > token.exp ? 0 : token.exp - nowInSeconds;
  const percentageExpired = Math.min(ageInSeconds, ttlInSeconds) / ttlInSeconds;
  return {
    percentageExpired,
    ageInSeconds,
    secondsUntilExpiration
  };
};

const localStorageNamespace = "@figs:";

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
const cookieManager = new Cookies();
const _TokenAuthStorageSingleton = class _TokenAuthStorageSingleton {
  constructor() {
    __publicField(this, "token", null);
    __publicField(this, "refreshToken", null);
  }
  static getInstance() {
    if (this.instance === null) {
      this.instance = new _TokenAuthStorageSingleton();
    }
    return this.instance;
  }
  getToken() {
    if (this._isLocalStorageDefined) {
      const cookieAuthToken = cookieManager.get(
        _TokenAuthStorageSingleton.AUTH_TOKEN_COOKIE_KEY
      );
      if (typeof cookieAuthToken === "string" && cookieAuthToken.length > 0) {
        return cookieAuthToken;
      } else {
        return window.localStorage.getItem(_TokenAuthStorageSingleton.AUTH_TOKEN_LOCAL_STORAGE_KEY);
      }
    }
    return this.token;
  }
  getRefreshToken() {
    if (this._isLocalStorageDefined) {
      const cookieRefreshToken = cookieManager.get(
        _TokenAuthStorageSingleton.REFRESH_TOKEN_COOKIE_KEY
      );
      if (typeof cookieRefreshToken === "string" && cookieRefreshToken.length > 0) {
        return cookieRefreshToken;
      } else {
        return window.localStorage.getItem(
          _TokenAuthStorageSingleton.REFRESH_TOKEN_LOCAL_STORAGE_KEY
        );
      }
    }
    return this.refreshToken;
  }
  getTokenStatus() {
    const token = this.getToken() ?? void 0;
    const refreshToken = this.getRefreshToken() ?? void 0;
    return getJwtAuthTokenStatus(token, refreshToken);
  }
  setToken(token, asBearer = false) {
    this.token = asBearer ? `Bearer ${token}` : token;
    if (this._isLocalStorageDefined && Boolean(token)) {
      window.localStorage.setItem(
        _TokenAuthStorageSingleton.AUTH_TOKEN_LOCAL_STORAGE_KEY,
        this.token
      );
      cookieManager.set(_TokenAuthStorageSingleton.AUTH_TOKEN_COOKIE_KEY, this.token, {
        secure: true,
        sameSite: "strict",
        path: "/",
        maxAge: 156e5
      });
    }
  }
  setRefreshToken(token) {
    this.refreshToken = token;
    if (this._isLocalStorageDefined && Boolean(token)) {
      window.localStorage.setItem(
        _TokenAuthStorageSingleton.REFRESH_TOKEN_LOCAL_STORAGE_KEY,
        this.refreshToken
      );
      cookieManager.set(_TokenAuthStorageSingleton.REFRESH_TOKEN_COOKIE_KEY, this.refreshToken, {
        secure: true,
        sameSite: "strict",
        path: "/",
        maxAge: 156e5
      });
    }
  }
  /** Get an appended token at a specific index */
  getAppendedTokenAtIndex(idx) {
    const currentToken = this.getToken();
    if (currentToken) {
      const tokenArray = currentToken.split(" ");
      if (idx in tokenArray && typeof tokenArray[idx] === "string") {
        return tokenArray[idx];
      }
    }
    return null;
  }
  appendToOrSetToken(token) {
    const currentToken = this.getToken();
    return currentToken ? this.setToken(currentToken.concat(` ${token}`)) : this.setToken(token);
  }
  clearToken() {
    this.token = null;
    if (this._isLocalStorageDefined) {
      window.localStorage.removeItem(_TokenAuthStorageSingleton.AUTH_TOKEN_LOCAL_STORAGE_KEY);
      cookieManager.remove(_TokenAuthStorageSingleton.AUTH_TOKEN_COOKIE_KEY, {
        path: "/"
      });
    }
  }
  clearRefreshToken() {
    this.refreshToken = null;
    if (this._isLocalStorageDefined) {
      window.localStorage.removeItem(_TokenAuthStorageSingleton.REFRESH_TOKEN_LOCAL_STORAGE_KEY);
      cookieManager.remove(_TokenAuthStorageSingleton.REFRESH_TOKEN_COOKIE_KEY, {
        path: "/"
      });
    }
  }
  clearTokens() {
    this.clearToken();
    this.clearRefreshToken();
  }
  hasAppendedToken() {
    const currentToken = this.getToken();
    if (this._isLocalStorageDefined && currentToken) {
      return currentToken.split(" ")?.length > 1;
    }
    return false;
  }
  get _isLocalStorageDefined() {
    return Boolean(
      typeof window !== "undefined" && typeof document !== "undefined" && window.localStorage
    );
  }
};
__publicField(_TokenAuthStorageSingleton, "instance", null);
__publicField(_TokenAuthStorageSingleton, "AUTH_TOKEN_LOCAL_STORAGE_KEY", `${localStorageNamespace}auth-token`);
__publicField(_TokenAuthStorageSingleton, "REFRESH_TOKEN_LOCAL_STORAGE_KEY", `${localStorageNamespace}refresh-token`);
__publicField(_TokenAuthStorageSingleton, "AUTH_TOKEN_COOKIE_KEY", `figs-auth-token`);
__publicField(_TokenAuthStorageSingleton, "REFRESH_TOKEN_COOKIE_KEY", `figs-refresh-token`);
let TokenAuthStorageSingleton = _TokenAuthStorageSingleton;

const authenticateCarica = async (config, force) => {
  const tokenSingleton = TokenAuthStorageSingleton.getInstance();
  let authToken = tokenSingleton.getToken();
  const decodedToken = authToken ? jwtAuthTokenFromString(authToken) : null;
  const isExpired = decodedToken && decodedToken.exp <= Date.now() / 1e3;
  if (!authToken || isExpired || force) {
    const fetch = typeof window === "undefined" ? unfetch : window.fetch;
    const response = await fetch(`${config.host}/authenticate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        email: config.email,
        password: config.password
      })
    });
    const data = await response.json();
    authToken = data.auth_token;
    tokenSingleton.setToken(authToken);
  }
  return authToken;
};

export { TokenAuthStorageSingleton, authenticateCarica, getJwtAuthTokenStatus, jwtAuthRefreshTokenFromString, jwtAuthTokenExpiryInfo, jwtAuthTokenFromString, localStorageNamespace };
